/**
 * ------------------------------------------------------------
 * CMS PARSER (Universal Contentful Data Transformer)
 * ------------------------------------------------------------
 *
 * Purpose:
 * - Recursively unwraps Contentful `fields`
 * - Converts Rich Text → Plain Text OR HTML
 * - Handles nested entries, arrays, and assets
 * - Works across all components/pages (generic utility)
 *
 * Usage:
 * import { parseCMSData } from "@/utils/cmsParser";
 *
 * const cleanData = parseCMSData(apiResponse);
 *
 * ------------------------------------------------------------
 */

/**
 * Check if object is Contentful Rich Text
 *
 * @param {any} obj
 * @returns {boolean}
 */
const isRichText = obj => {
  return obj?.nodeType === "document" && Array.isArray(obj?.content);
};

/**
 * Extract plain text from Contentful Rich Text
 *
 * @param {Object} richText
 * @returns {string}
 */
const richTextToPlain = richText => {
  const extract = (nodes = []) =>
    nodes
      .map(node => {
        if (node.nodeType === "text") return node.value;

        if (node.content) return extract(node.content);

        return "";
      })
      .join("");

  return extract(richText.content).trim();
};

/**
 * Convert Rich Text → Basic HTML (optional)
 *
 * NOTE: This is a simple converter.
 * For advanced rendering use @contentful/rich-text-react-renderer
 *
 * @param {Object} richText
 * @returns {string}
 */
const richTextToHTML = richText => {
  const extract = (nodes = []) =>
    nodes
      .map(node => {
        if (node.nodeType === "text") return node.value;

        if (node.nodeType === "paragraph") {
          return `<p>${extract(node.content)}</p>`;
        }

        if (node.nodeType === "heading-1") {
          return `<h1>${extract(node.content)}</h1>`;
        }

        if (node.nodeType === "heading-2") {
          return `<h2>${extract(node.content)}</h2>`;
        }

        if (node.content) return extract(node.content);

        return "";
      })
      .join("");

  return extract(richText.content);
};

/**
 * Check if object is a Contentful Asset (image/file)
 *
 * @param {Object} obj
 * @returns {boolean}
 */
const isAsset = obj => {
  return obj?.fields?.file?.url;
};

/**
 * Normalize Contentful Asset
 *
 * @param {Object} asset
 * @returns {{url: string, title: string, description: string}}
 */
const parseAsset = asset => {
  return {
    url: asset.fields.file.url,
    title: asset.fields.title || "",
    description: asset.fields.description || "",
  };
};

/**
 * ------------------------------------------------------------
 * MAIN PARSER FUNCTION
 * ------------------------------------------------------------
 */

/**
 * Recursively parses CMS data into clean JSON
 *
 * @param {any} input - Raw CMS response
 * @param {Object} [options]
 * @param {"plain"|"html"} [options.richTextFormat="plain"]
 *
 * @returns {any} Cleaned data
 */
export const parseCMSData = (input, options = {}) => {
  const { richTextFormat = "plain" } = options;

  /**
   * Internal recursive function
   *
   * @param {any} value
   * @returns {any}
   */
  const parse = value => {
    // Handle arrays
    if (Array.isArray(value)) {
      return value.map(parse);
    }

    // Handle objects
    if (value && typeof value === "object") {
      // 1. Asset handling
      if (isAsset(value)) {
        return parseAsset(value);
      }

      // 2. unwrap fields
      if (value.fields) {
        return parse(value.fields);
      }

      // 3. Rich text handling
      if (isRichText(value)) {
        return richTextFormat === "html"
          ? richTextToHTML(value)
          : richTextToPlain(value);
      }

      // 4. Normal object recursion
      const output = {};
      for (const key in value) {
        output[key] = parse(value[key]);
      }

      return output;
    }

    // primitives (string, number, boolean, null)
    return value;
  };

  return parse(input);
};

/**
 * Formats an image URL by removing unnecessary encoding
 * @param {string} imageSrc
 * @returns {string|null}
 */
export const imageUrlFormatter = imageSrc => {
  if (!imageSrc) return null;
  return imageSrc.replace(/%22/g, "");
};
